import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { getBrandLogos } from "../../helpers/brandPortal";
import { persistTenantSession } from "../../helpers/api_helper";
import {
  acceptOwnerInvite,
  validateOwnerInvite,
} from "../../helpers/owner_invite_helper";

/**
 * LAC-074: owner provisionado pelo SA define a senha pelo link do e-mail.
 * Rota pública `/owner-invite/accept?token=…` — espelha AcceptInvite da equipe.
 */
const AcceptOwnerInvite = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token")?.trim() || "", [params]);

  const [email, setEmail] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(Boolean(token));
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPreviewLoading(false);
      setPreviewError(t("ownerInvite.missingToken"));
      return;
    }
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const res = await validateOwnerInvite(token);
        if (cancelled) {
          return;
        }
        if (!res?.success || !res.data) {
          setPreviewError(t("ownerInvite.invalid"));
          return;
        }
        setEmail(res.data.email);
        setTenantName(res.data.tenant_name);
      } catch (e: unknown) {
        if (!cancelled) {
          const msg =
            e && typeof e === "object" && "message" in e
              ? String((e as { message?: string }).message)
              : null;
          setPreviewError(msg || t("ownerInvite.invalid"));
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!token) {
      setError(t("ownerInvite.missingToken"));
      return;
    }
    if (password.length < 8) {
      setError(t("ownerInvite.passwordMin"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("ownerInvite.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      const res = await acceptOwnerInvite({
        token,
        password,
        password_confirmation: confirmPassword,
      });
      if (!res?.success || !res.data?.access_token) {
        setError(t("ownerInvite.error"));
        return;
      }
      // Mesmo envelope do login — entra direto no painel.
      persistTenantSession(res.data);
      navigate("/dashboard", { replace: true });
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : null;
      setError(msg || t("ownerInvite.error"));
    } finally {
      setBusy(false);
    }
  };

  document.title = t("ownerInvite.documentTitle");

  return (
    <ParticlesAuth>
      <div className="auth-page-content mt-lg-5">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4">
                <Link to="/login">
                  <img
                    src={getBrandLogos().dark}
                    alt={getBrandLogos().alt}
                    height="20"
                  />
                </Link>
              </div>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4 card-bg-fill">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">{t("ownerInvite.title")}</h5>
                    <p className="text-muted">
                      {tenantName
                        ? t("ownerInvite.subtitleTenant", { tenant: tenantName })
                        : t("ownerInvite.subtitle")}
                    </p>
                  </div>

                  {previewLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                    </div>
                  ) : null}

                  {!previewLoading && previewError ? (
                    <Alert color="warning">{previewError}</Alert>
                  ) : null}

                  {!previewLoading && !previewError ? (
                    <>
                      {email ? (
                        <p className="text-muted small mb-3">
                          {t("ownerInvite.emailLabel", { email })}
                        </p>
                      ) : null}
                      {error ? <Alert color="danger">{error}</Alert> : null}
                      <Form className="mt-2" onSubmit={submit}>
                        <div className="mb-3">
                          <Label htmlFor="owner-invite-password">
                            {t("ownerInvite.password")}
                          </Label>
                          <Input
                            id="owner-invite-password"
                            required
                            minLength={8}
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                          <div className="form-text">
                            {t("ownerInvite.passwordHelp")}
                          </div>
                        </div>
                        <div className="mb-3">
                          <Label htmlFor="owner-invite-confirm">
                            {t("ownerInvite.confirm")}
                          </Label>
                          <Input
                            id="owner-invite-confirm"
                            required
                            type="password"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                        </div>
                        <Button
                          color="success"
                          className="w-100"
                          disabled={busy || !token}
                        >
                          {busy ? (
                            <Spinner size="sm" className="me-2" />
                          ) : null}
                          {t("ownerInvite.submit")}
                        </Button>
                      </Form>
                    </>
                  ) : null}
                </CardBody>
              </Card>
              <div className="text-center mt-4">
                <Link to="/login">{t("ownerInvite.backLogin")}</Link>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default AcceptOwnerInvite;
